<template>
  <div class="contract-apply">
    <h3>申请修改</h3>
    <!-- 面包屑导航 -->
    <el-breadcrumb separator="/" class="breadcrumb">
      <el-breadcrumb-item :to="{ path: '/yj/work' }">合同管理</el-breadcrumb-item>
      <el-breadcrumb-item>申请修改</el-breadcrumb-item>
    </el-breadcrumb>

    <div class="apply-container">
      <div v-if="loading" class="loading-container">
        <el-skeleton :rows="6" animated />
      </div>

      <div v-else-if="!contractInfo" class="empty-state">
        <el-empty description="未选择合同" />
        <el-button type="primary" @click="goBack">返回合同列表</el-button>
      </div>

      <div v-else>
        <!-- 合同基本信息 -->
        <el-card class="contract-info-card">
          <div slot="header" class="card-header">
            <span class="title">合同信息</span>
            <el-tag v-if="isLocked" type="warning">已有待审核申请</el-tag>
            <el-button v-else type="primary" size="small" @click="goBack" plain>
              选择其他合同
            </el-button>
          </div>

          <div class="contract-details">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="项目名称">{{ contractInfo.WorkName }}</el-descriptions-item>
              <el-descriptions-item label="归档编号">{{ contractInfo.ReturnId || '-' }}</el-descriptions-item>
              <el-descriptions-item label="建设单位">{{ contractInfo.build_company }}</el-descriptions-item>
              <el-descriptions-item label="承接单位">{{ contractInfo.SubjectName || '-' }}</el-descriptions-item>
              <el-descriptions-item label="截止时间">{{ formatDate(contractInfo.DeadLine) }}</el-descriptions-item>
              <el-descriptions-item label="当前状态">
                <el-tag :type="getStatusTagType(contractInfo)">
                  {{ getStatusText(contractInfo) }}
                </el-tag>
              </el-descriptions-item>
            </el-descriptions>
          </div>
        </el-card>

        <!-- 修改申请表单 -->
        <el-card class="apply-form-card">
          <div slot="header" class="card-header">
            <span class="title">修改申请</span>
            <el-button v-if="pendingApply && !isSubmitting" type="warning" size="small" @click="showPendingApply">
              查看待审核申请
            </el-button>
          </div>

          <div v-if="isLocked && pendingApply" class="locked-warning">
            <el-alert title="该合同已有待审核的申请" type="warning" :closable="false" show-icon>
              <div class="warning-content">
                <p>申请时间：{{ formatDateTime(pendingApply.applyTime) }}</p>
                <p>申请人：{{ pendingApply.applyUserId ? '用户' + pendingApply.applyUserId : '未知' }}</p>
                <p>如需修改，请联系管理员处理当前申请</p>
              </div>
            </el-alert>
          </div>

          <div v-else>
            <el-form ref="applyForm" :model="applyForm" :rules="formRules" label-width="120px" size="small">
              <!-- 申请理由 -->
              <el-form-item label="申请理由" prop="applyReason" required>
                <el-input type="textarea" v-model="applyForm.applyReason" placeholder="请简要说明修改原因" :rows="3"
                  maxlength="500" show-word-limit />
              </el-form-item>

              <!-- 修改字段表格 -->
              <!-- 修改修改内容表格 -->
              <el-table :data="modifyFields" border size="small" class="modify-content-table">
                <el-table-column prop="fieldName" label="字段" width="150" />
                <el-table-column prop="oldValue" label="当前值" width="200">
                  <template slot-scope="scope">
                    <span class="old-value">{{ scope.row.oldValue || '-' }}</span>
                  </template>
                </el-table-column>
                <el-table-column prop="newValue" label="修改为" width="200">
                  <template slot-scope="scope">
                    <!-- 截止时间 - 使用日期选择器 -->
                    <template v-if="scope.row.field === 'DeadLine'">
                      <el-date-picker v-model="scope.row.newValue" type="date" placeholder="选择截止日期" style="width: 100%"
                        format="yyyy-MM-dd" value-format="yyyy-MM-dd" size="mini"
                        @change="handleFieldChange(scope.row)" />
                    </template>

                    <!-- 备注 - 使用文本域 -->
                    <template v-else-if="scope.row.fieldName === '备注'">
                      <el-input type="textarea" v-model="scope.row.newValue" :placeholder="`请输入${scope.row.fieldName}`"
                        :rows="2" size="mini" maxlength="2000" show-word-limit
                        @input="handleFieldChange(scope.row)" />
                    </template>

                    <!-- 服务内容 - 下拉选择 -->
                    <template v-else-if="scope.row.fieldName === '服务内容'">
                      <el-select v-model="scope.row.newValue" placeholder="请选择服务内容" style="width: 100%"
                        filterable size="mini" @change="handleFieldChange(scope.row)">
                        <el-option v-for="item in serviceContentList" :key="item.id" :label="item.ContentName"
                          :value="item.ContentName" />
                      </el-select>
                    </template>

                    <!-- 价格字段 - 添加单位 -->
                    <template v-else-if="['招标控制价', '中标价', '合同金额', '已开票金额', '评委费'].includes(scope.row.fieldName)">
                      <el-input v-model="scope.row.newValue" :placeholder="`请输入${scope.row.fieldName}`" size="mini"
                        @input="handleFieldChange(scope.row)">
                        <template slot="append">元</template>
                      </el-input>
                    </template>

                    <!-- 其他字段 - 普通输入框 -->
                    <template v-else>
                      <el-input v-model="scope.row.newValue" :placeholder="`请输入${scope.row.fieldName}`" size="mini"
                        @input="handleFieldChange(scope.row)" />
                    </template>
                  </template>
                </el-table-column>
                <el-table-column label="操作" width="80" align="center">
                  <template slot-scope="scope">
                    <el-button type="text" size="mini" @click="resetField(scope.row)" v-if="scope.row.isChanged">
                      重置
                    </el-button>
                  </template>
                </el-table-column>
              </el-table>

              <!-- 表单操作按钮 -->
              <el-form-item>
                <el-button @click="goBack">取消</el-button>
                <el-button type="primary" @click="submitApply" :loading="isSubmitting" :disabled="!hasChanges">
                  提交申请
                </el-button>
                <el-button type="info" @click="resetForm" :disabled="!hasChanges">
                  重置
                </el-button>
              </el-form-item>
            </el-form>
          </div>
        </el-card>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  getContractDetail,
  checkContractApplyable,
  submitContractApply,
  getAllWorkList,
  initServiceContent
} from '../api';

export default {
  name: 'ContractApply',

  data() {
    return {
      loading: true,
      contractId: null,
      contractInfo: null,
      isLocked: false,
      pendingApply: null,
      serviceContentList: [],

      // 申请表单
      applyForm: {
        applyReason: '',
      },

      // 修改字段列表
      modifyFields: [
        { field: 'WorkName', fieldName: '项目名称', oldValue: '', newValue: '', isChanged: false },
        { field: 'ReturnId', fieldName: '归档编号', oldValue: '', newValue: '', isChanged: false },
        { field: 'content', fieldName: '服务内容', oldValue: '', newValue: '', isChanged: false },
        { field: 'build_company', fieldName: '建设单位', oldValue: '', newValue: '', isChanged: false },
        { field: 'DeadLine', fieldName: '截止时间', oldValue: '', newValue: '', isChanged: false, type: 'date' },
        { field: 'win_company', fieldName: '中标/送审单位', oldValue: '', newValue: '', isChanged: false },
        { field: 'FirstPrice', fieldName: '招标控制价', oldValue: '', newValue: '', isChanged: false },
        { field: 'SecondPrice', fieldName: '中标价', oldValue: '', newValue: '', isChanged: false },
        { field: 'HtPrice', fieldName: '合同金额', oldValue: '', newValue: '', isChanged: false },
        { field: 'PayPrice', fieldName: '已开票金额', oldValue: '', newValue: '', isChanged: false },
        { field: 'PingPrice', fieldName: '评委费', oldValue: '', newValue: '', isChanged: false },
        { field: 'note', fieldName: '备注', oldValue: '', newValue: '', isChanged: false },
      ],

      formRules: {
        applyReason: [
          { required: true, message: '请输入申请理由', trigger: 'blur' },
          { min: 3, message: '申请理由至少3个字符', trigger: 'blur' }
        ]
      },

      isSubmitting: false
    };
  },

  computed: {
    ...mapGetters(['name', 'userId', 'roleId']),

    hasChanges() {
      return this.modifyFields.some(field => field.isChanged) && this.applyForm.applyReason.trim().length >= 3;
    }
  },

  created() {
    this.loadServiceContents();
    this.contractId = this.$route.query.contractId;

    if (!this.contractId) {
      this.$message.error('未指定合同');
      this.$router.push('/yj/work');
      return;
    }

    // 1. 先尝试从sessionStorage获取合同数据（确保contractId严格匹配）
    const savedContract = sessionStorage.getItem('currentContract');
    const savedContractId = sessionStorage.getItem('contractId');

    // 严格匹配contractId（字符串和数字都要匹配）
    const contractIdMatch = savedContractId && (
      savedContractId == this.contractId || 
      savedContractId === String(this.contractId) ||
      parseInt(savedContractId) === parseInt(this.contractId)
    );

    if (savedContract && contractIdMatch) {
      try {
        const parsedContract = JSON.parse(savedContract);
        
        // 再次验证合同ID是否匹配
        if (parsedContract.id == this.contractId || parsedContract.id === parseInt(this.contractId)) {
          this.contractInfo = parsedContract;
          console.log('从sessionStorage加载合同数据:', this.contractInfo);
          console.log('合同ID验证通过:', this.contractInfo.id, '===', this.contractId);
          this.fillOldValues();
          this.loading = false;

          // 清除存储的数据
          sessionStorage.removeItem('currentContract');
          sessionStorage.removeItem('contractId');

          // 异步检查申请状态
          this.checkApplyStatusAsync();
          return;
        } else {
          console.warn('sessionStorage中的合同ID不匹配，将重新加载');
          console.warn('存储的ID:', parsedContract.id, '期望的ID:', this.contractId);
          // 清除不匹配的数据
          sessionStorage.removeItem('currentContract');
          sessionStorage.removeItem('contractId');
        }
      } catch (e) {
        console.error('解析存储的合同数据失败:', e);
        // 清除损坏的数据
        sessionStorage.removeItem('currentContract');
        sessionStorage.removeItem('contractId');
      }
    } else {
      if (savedContractId) {
        console.warn('sessionStorage中的contractId不匹配:', savedContractId, '期望:', this.contractId);
        // 清除不匹配的数据
        sessionStorage.removeItem('currentContract');
        sessionStorage.removeItem('contractId');
      }
    }

    // 2. 如果没有存储的数据或数据不匹配，调用API加载
    this.loadContractInfo();
  },

  // 添加异步检查状态的方法
  async checkApplyStatusAsync() {
    try {
      const checkRes = await checkContractApplyable({
        contractId: this.contractId
      });

      if (checkRes.code === 0) {
        this.isLocked = false;
      } else {
        this.isLocked = true;
        this.pendingApply = checkRes.data || null;
        this.$message.warning(checkRes.msg);
      }
    } catch (error) {
      console.error('检查申请状态失败:', error);
    }
  },

  methods: {
    // 加载服务内容下拉列表
    async loadServiceContents() {
      try {
        const res = await initServiceContent();
        if (res && res.msg) {
          const obj = JSON.parse(res.msg);
          this.serviceContentList = (obj.ServiceContent && Array.isArray(obj.ServiceContent))
            ? obj.ServiceContent
            : [];
        }
      } catch (e) {
        console.error('加载服务内容失败:', e);
        this.serviceContentList = [];
      }
    },

    // 加载合同信息
    async loadContractInfo() {
      this.loading = true;
      try {
        // 先检查是否可以申请
        const checkRes = await checkContractApplyable({
          contractId: this.contractId
        });

        if (checkRes.code === 0) {
          this.isLocked = false;
          // 加载合同详情
          await this.loadContractDetail();
        } else {
          this.isLocked = true;
          this.pendingApply = checkRes.data || null;
          // 仍然加载合同详情用于显示
          await this.loadContractDetail();
          this.$message.warning(checkRes.msg);
        }
      } catch (error) {
        console.error('加载合同信息失败:', error);
        this.$message.error('加载合同信息失败');
      } finally {
        this.loading = false;
      }
    },

    // 加载合同详情
    async loadContractDetail() {
      try {
        console.log('尝试加载合同详情，ID:', this.contractId);

        // 方法1：尝试通过搜索关键词找到合同（使用项目名称等）
        // 先尝试获取所有合同，然后精确匹配ID
        let contract = null;
        let page = 1;
        const limit = 50; // 增大每页数量
        let found = false;

        // 最多搜索5页，避免无限循环
        while (page <= 5 && !found) {
          const res = await getAllWorkList({
            limit: limit,
            page: page
          });

          if (res.code === 0 && res.data && Array.isArray(res.data)) {
            // 精确匹配ID
            contract = res.data.find(item => item.id == this.contractId || item.id === parseInt(this.contractId));
            
            if (contract) {
              found = true;
              console.log(`在第${page}页找到合同，ID: ${contract.id}, 项目名称: ${contract.WorkName}`);
              break;
            }

            // 如果当前页数据少于limit，说明已经是最后一页
            if (res.data.length < limit) {
              break;
            }
          } else {
            break;
          }
          
          page++;
        }

        if (contract) {
          this.contractInfo = contract;
          this.fillOldValues();
          console.log('合同详情加载成功:', this.contractInfo);
          return;
        }

        // 方法2：如果通过搜索找不到，尝试使用回退数据
        console.warn('通过API搜索未找到合同，尝试使用回退数据');
        this.fallbackToQueryData();

      } catch (error) {
        console.error('加载合同详情失败:', error);
        this.$message.error('加载合同详情失败');

        // 使用回退数据
        this.fallbackToQueryData();
      }
    },

    // 获取用户ID的方法
    getCurrentUserId() {
      // 直接使用 username 作为用户ID
      const userId = this.username;
      console.log('使用 username 作为用户ID:', userId);
      return userId;
    },

    // 回退加载方法
    fallbackLoadContract() {
      // 尝试从路由参数或其他地方获取合同信息
      const contractFromState = this.$store.state.currentContract;
      const contractFromSession = sessionStorage.getItem('currentContract');

      if (contractFromState && contractFromState.id == this.contractId) {
        this.contractInfo = contractFromState;
        this.fillOldValues();
        this.$message.warning('使用缓存数据，部分信息可能不完整');
      } else if (contractFromSession) {
        try {
          const contractData = JSON.parse(contractFromSession);
          if (contractData.id == this.contractId) {
            this.contractInfo = contractData;
            this.fillOldValues();
            this.$message.warning('使用会话数据，部分信息可能不完整');
          }
        } catch (e) {
          console.error('解析会话数据失败:', e);
        }
      }

      // 如果还是没找到，显示错误
      if (!this.contractInfo) {
        this.$message.error('无法加载合同详情，请返回重试');
      }
    },

    // 添加回退方法
    fallbackToQueryData() {
      // 从路由参数构建基础合同信息
      this.contractInfo = {
        id: this.contractId,
        WorkName: decodeURIComponent(this.$route.query.workName || ''),
        ReturnId: decodeURIComponent(this.$route.query.returnId || ''),
        build_company: decodeURIComponent(this.$route.query.company || ''),
        // 其他字段设为默认值
        SubjectName: '',
        DeadLine: '',
        content: '',
        win_company: '',
        FirstPrice: null,
        SecondPrice: null,
        HtPrice: null,
        PayPrice: null,
        PingPrice: null,
        note: ''
      };

      this.fillOldValues();
      this.$message.warning('合同详情加载不完整，部分信息可能缺失');
    },

    // 填充旧值

    fillOldValues() {
      if (!this.contractInfo) return;

      this.modifyFields.forEach(field => {
        const value = this.contractInfo[field.field];
        if (value !== undefined && value !== null) {
          // 格式化显示
          if (field.field === 'DeadLine' && value) {
            // 确保日期格式正确
            if (typeof value === 'string') {
              field.oldValue = value.length >= 10 ? value.substring(0, 10) : value;
            } else if (value instanceof Date) {
              field.oldValue = this.formatDate(value);
            } else {
              field.oldValue = String(value);
            }
          } else if (['FirstPrice', 'SecondPrice', 'HtPrice', 'PayPrice', 'PingPrice'].includes(field.field)) {
            field.oldValue = value ? '¥' + Number(value).toFixed(2) : '-';
          } else {
            field.oldValue = String(value);
          }
        } else {
          field.oldValue = '-';
        }
        field.newValue = '';
        field.isChanged = false;
      });
    },

    // 字段变化处理
    // 字段变化处理
    handleFieldChange(field) {
      const oldValue = this.contractInfo[field.field];
      let oldValueStr = '';

      if (oldValue !== undefined && oldValue !== null) {
        if (field.field === 'DeadLine') {
          // 日期字段特殊处理
          if (oldValue) {
            // 确保日期格式统一
            if (typeof oldValue === 'string') {
              oldValueStr = oldValue.length >= 10 ? oldValue.substring(0, 10) : oldValue;
            } else if (oldValue instanceof Date) {
              oldValueStr = this.formatDate(oldValue);
            } else {
              oldValueStr = String(oldValue);
            }
          } else {
            oldValueStr = '';
          }
        } else if (['FirstPrice', 'SecondPrice', 'HtPrice', 'PayPrice', 'PingPrice'].includes(field.field)) {
          oldValueStr = oldValue ? '¥' + Number(oldValue).toFixed(2) : '-';
        } else {
          oldValueStr = String(oldValue);
        }
      }

      // 判断是否发生变化
      if (field.field === 'DeadLine') {
        // 日期字段比较：去除时间部分，只比较年月日
        const newDateStr = field.newValue ? field.newValue.substring(0, 10) : '';
        const oldDateStr = oldValueStr ? oldValueStr.substring(0, 10) : '';
        field.isChanged = newDateStr !== '' && newDateStr !== oldDateStr;
      } else {
        field.isChanged = field.newValue.trim() !== '' && field.newValue !== oldValueStr;
      }
    },

    // 重置单个字段
    resetField(field) {
      if (field.field === 'DeadLine') {
        field.newValue = ''; // 清空日期选择器
      } else {
        field.newValue = '';
      }
      field.isChanged = false;
    },

    // 重置整个表单
    resetForm() {
      this.applyForm.applyReason = '';
      this.modifyFields.forEach(field => {
        field.newValue = '';
        field.isChanged = false;
      });
      this.$message.info('表单已重置');
    },

    // 提交申请
    async submitApply() {
  try {
    await this.$refs.applyForm.validate();

    if (!this.hasChanges) {
      this.$message.warning('请至少修改一个字段');
      return;
    }

    console.log('=== 提交申请 ===');
    
    // 使用 URLSearchParams 构建参数，与后端 getPara() 方法兼容
    const params = new URLSearchParams();
    params.append('contractId', this.contractId);
    params.append('applyReason', this.applyForm.applyReason);
    
    // 添加修改的字段
    const changedFields = [];
    this.modifyFields.forEach(field => {
      if (field.isChanged && field.newValue.trim() !== '') {
        params.append(field.field, field.newValue.trim());
        changedFields.push(field.fieldName);
      }
    });

    console.log('提交的参数:', params.toString());
    console.log('修改的字段:', changedFields);

    this.isSubmitting = true;
    
    // 使用 post 方法发送 URLSearchParams
    const res = await submitContractApply(params);

    if (res.code === 0) {
      this.$message.success('申请提交成功，请等待审核');
      setTimeout(() => {
        this.$router.push('/yj/work');
      }, 1500);
    } else {
      this.$message.error(res.msg || '提交失败');
      console.error('API返回错误:', res);
    }
  } catch (error) {
    console.error('提交申请失败:', error);
    this.$message.error('提交失败，请重试');
  } finally {
    this.isSubmitting = false;
  }
},


    // 查看待审核申请
    showPendingApply() {
      if (this.pendingApply && this.pendingApply.applyId) {
        this.$router.push({
          path: '/contract/apply/detail',
          query: { applyId: this.pendingApply.applyId }
        });
      }
    },

    // 返回合同列表
    goBack() {
      this.$router.push('/yj/work');
    },

    // 格式化日期
    formatDate(date) {
      if (!date) return '-';
      if (typeof date === 'string' && date.length >= 10) {
        return date.substring(0, 10);
      }
      if (date instanceof Date) {
        return date.toISOString().substring(0, 10);
      }
      return '-';
    },

    // 格式化日期时间
    formatDateTime(datetime) {
      if (!datetime) return '-';
      return new Date(datetime).toLocaleString();
    },

    // 获取状态文本
    getStatusText(contract) {
      const isCompleted = contract.is_completed || contract.isCompleted;
      if (isCompleted) return '已完成';

      if (!contract.DeadLine) return '正常';

      const deadLine = new Date(contract.DeadLine);
      const today = new Date();
      const diffDays = Math.ceil((deadLine.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

      if (diffDays < 0) return '超期未完成';
      if (diffDays <= 5) return '紧急';
      if (diffDays <= 10) return '紧张';
      return '正常';
    },

    // 获取状态标签类型
    getStatusTagType(contract) {
      const isCompleted = contract.is_completed || contract.isCompleted;
      if (isCompleted) return 'success';

      if (!contract.DeadLine) return 'info';

      const deadLine = new Date(contract.DeadLine);
      const today = new Date();
      const diffDays = Math.ceil((deadLine.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

      if (diffDays < 0) return 'danger';
      if (diffDays <= 5) return 'danger';
      if (diffDays <= 10) return 'warning';
      return 'info';
    }
  }
};
</script>

<style scoped>
.contract-apply {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: calc(100vh - 60px);
}

.breadcrumb {
  margin-bottom: 20px;
}

.apply-container {
  max-width: 1200px;
  margin: 0 auto;
}

.loading-container {
  padding: 40px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.empty-state {
  text-align: center;
  padding: 60px 0;
}

.contract-info-card,
.apply-form-card {
  margin-bottom: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.contract-details {
  padding: 10px 0;
}

.locked-warning {
  margin-bottom: 20px;
}

.warning-content {
  margin-top: 10px;
  font-size: 13px;
  color: #606266;
}

.warning-content p {
  margin: 5px 0;
}

.modify-table {
  background: white;
  border-radius: 4px;
  border: 1px solid #ebeef5;
}

.modify-content-table {
  margin: 0;
}

.old-value {
  color: #909399;
  font-style: italic;
}

.modify-hint {
  margin-top: 15px;
}

.el-form-item:last-child {
  margin-bottom: 0;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
}

/* 日期选择器样式优化 */
:deep(.el-date-editor.el-input) {
  width: 100%;
}

/* 价格输入框单位样式 */
:deep(.el-input-group__append) {
  padding: 0 8px;
  background-color: #f5f7fa;
}

/* 表格内输入框样式 */
.modify-content-table :deep(.el-input__inner) {
  border: none;
  padding: 0 5px;
}

.modify-content-table :deep(.el-textarea__inner) {
  border: none;
  padding: 5px;
  font-size: 12px;
}

/* 日期选择器在表格中的样式 */
.modify-content-table :deep(.el-date-editor) {
  width: 100%;
}

.modify-content-table :deep(.el-date-editor .el-input__inner) {
  padding-right: 25px;
}

/* 响应式适配 */
@media screen and (max-width: 768px) {
  .contract-apply {
    padding: 10px;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }


  .el-descriptions {
    font-size: 12px;


    .modify-content-table :deep(.el-date-editor) {
      font-size: 12px;
    }

    .modify-content-table :deep(.el-input__inner) {
      height: 28px;
      line-height: 28px;
    }
  }

}
</style>